// src/ui/Pill.tsx (T1): status pills, counts and small tags. Status is always the navy tint (no green, no gold).
// Root classes: hb-pill, hb-badge, hb-unread, hb-new-dot, hb-tag-split, hb-ai-label.
import type { ComponentChildren } from "preact";
import { cx } from "./cx";
import { Icon } from "./Icon";

export type PillTone = "navy" | "solid" | "line";

/** A quiet square status tag ("Your turn", "New inquiry", "Draft"). `dot` adds a leading dot. */
export function Pill(props: { tone?: PillTone; dot?: boolean; class?: string; children?: ComponentChildren }) {
  const tone = props.tone ?? "navy";
  return <span class={cx("hb-pill", "pill", `pill-${tone}`, props.dot && "pill-dot", props.class)}>{props.children}</span>;
}

/** A round count ("1" unread). `label` is the accessible text ("1 unread"). */
export function Badge(props: { count: number; label?: string; class?: string }) {
  if (props.count <= 0) return null;
  return (
    <span class={cx("hb-badge", "badge", props.class)} aria-label={props.label ?? String(props.count)}>
      {props.count > 99 ? "99+" : props.count}
    </span>
  );
}

/** The square navy "New" marker. */
export function Unread(props: { children?: ComponentChildren }) {
  return <span class="hb-unread unread">{props.children ?? "New"}</span>;
}

/** An 8px navy dot for new activity; give it a label when nothing else says "new". */
export function NewDot(props: { label?: string }) {
  return <span class="hb-new-dot new-dot" role={props.label ? "img" : undefined} aria-label={props.label} aria-hidden={props.label ? undefined : "true"} />;
}

/** The couple see an idea differently. Labeled "Two views", never "Split" (the board's brief: no disagree, split or conflict). */
export function SplitTag(props: { children?: ComponentChildren }) {
  return (
    <span class="hb-tag-split tag-split">
      <Icon name="split" />
      {props.children ?? "Two views"}
    </span>
  );
}

/**
 * AI is always labeled (spec 2.9). `tag` is the bordered tag (default), `inline` the quieter line form used over
 * drafts ("AI draft · edit before sending"), `mark` the icon-only square with the label as its accessible name.
 */
export function AiLabel(props: { label: string; variant?: "tag" | "inline" | "mark"; class?: string }) {
  const v = props.variant ?? "tag";
  if (v === "mark") {
    return (
      <span class={cx("hb-ai-label", "ai-mark", props.class)} role="img" aria-label={props.label} title={props.label}>
        <Icon name="spark" />
      </span>
    );
  }
  return (
    <span class={cx("hb-ai-label", v === "tag" ? "tag-ai" : "ai-draft-label", props.class)}>
      <Icon name="spark" />
      {props.label}
    </span>
  );
}
